import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { MdVisibility, MdPictureAsPdf, MdDraw, MdExpandMore, MdExpandLess } from 'react-icons/md';
import theme from '../theme';
import { downloadPackPdf } from './EvidencePackDetailDrawer';

const COLORS = theme.colors;

function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '—';
  return `${Math.round(Number(seconds) / 60)} min`;
}

/**
 * @param {{
 *   cycles: object[],
 *   loading?: boolean,
 *   error?: string,
 *   email: string,
 *   onView: (historyId: string) => void,
 *   onSign: (historyId: string) => void,
 *   onRefresh?: () => void,
 *   canExport?: boolean,
 *   canSign?: boolean,
 * }} props
 */
export default function EvidencePacksPanel({
  cycles = [],
  loading,
  error,
  email,
  onView,
  onSign,
  onRefresh,
  canExport = false,
  canSign = false,
}) {
  const [busyId, setBusyId] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleDownload = async (historyId) => {
    if (!canExport) return;
    setBusyId(historyId);
    try {
      await downloadPackPdf(historyId, email);
      onRefresh?.();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${COLORS.borderDefault}`,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: expanded ? 2 : 0,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>
            Evidence packs
          </Typography>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5, maxWidth: 560 }}>
            One PDF per UV-C cycle (room turnover) with obligation-anchor citations and IPC sign-off —
            not a multi-cycle committee bundle. Anchors document organisational duties; they do not
            claim NABH or JCI accreditation compliance.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          endIcon={expanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
          onClick={() => setExpanded((v) => !v)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: `${theme.radius.md}px`,
            flexShrink: 0,
            background: theme.gradients.primary,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              background: theme.gradients.primaryHover,
              boxShadow: 'none',
            },
          }}
        >
          {expanded ? 'Close' : 'Open'}
        </Button>
      </Box>

      {expanded ? (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : cycles.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, py: 2 }}>
              No disinfection cycles in the last six months.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Coverage</TableCell>
                    <TableCell>UV exposure (proxy)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cycles.map((c) => (
                    <TableRow key={c.historyId} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c.roomName || '—'}</Typography>
                        <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                          {c.robotId || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {c.cycleDate ? new Date(c.cycleDate).toLocaleString('en-GB') : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={c.status || '—'}
                          color={c.status === 'Completed' ? 'success' : 'warning'}
                        />
                        {c.signed ? (
                          <Chip
                            size="small"
                            label="Signed"
                            color="success"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                          />
                        ) : null}
                        {c.incompleteRecord ? (
                          <Chip
                            size="small"
                            label="Incomplete — no manual clean"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 0.5, mt: 0.5, display: 'block', width: 'fit-content' }}
                          />
                        ) : null}
                        {(c.openFollowUpCount ?? 0) > 0 ? (
                          <Chip
                            size="small"
                            label="Manual wipe needed"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 0.5, mt: 0.5, display: 'block', width: 'fit-content' }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {(() => {
                          const pct = c.completionPercent ?? c.pack?.completionPercent;
                          if (pct != null) return `${pct}%`;

                          const completed = Number(c.completedObject ?? c.pack?.completedObject ?? 0);
                          const total = Number(c.totalObject ?? c.pack?.totalObject ?? 0);
                          if (total > 0) return `${completed}/${total}`;

                          return '—';
                        })()}
                        <Typography
                          component="span"
                          sx={{ fontSize: 11, color: COLORS.textSecondary, display: 'block' }}
                        >
                          objects
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {formatDuration(c.uvLightUsageInSeconds)}
                        <Typography
                          component="span"
                          sx={{ fontSize: 11, color: COLORS.textSecondary, display: 'block' }}
                        >
                          runtime · not mJ/cm²
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            startIcon={<MdVisibility size={14} />}
                            sx={{ textTransform: 'none' }}
                            onClick={() => onView(c.historyId)}
                          >
                            View
                          </Button>
                          {canExport ? (
                            <Button
                              size="small"
                              startIcon={
                                busyId === c.historyId ? (
                                  <CircularProgress size={12} />
                                ) : (
                                  <MdPictureAsPdf size={14} />
                                )
                              }
                              sx={{ textTransform: 'none' }}
                              disabled={busyId === c.historyId}
                              onClick={() => handleDownload(c.historyId)}
                            >
                              PDF
                            </Button>
                          ) : null}
                          {canSign && !c.signed ? (
                            <Button
                              size="small"
                              startIcon={<MdDraw size={14} />}
                              sx={{ textTransform: 'none' }}
                              onClick={() => onSign(c.historyId)}
                            >
                              Sign
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      ) : null}
    </Paper>
  );
}
